import { Injectable } from '@angular/core';

export interface ToggleStates {
  [key: string]: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ToggleStateService {

  private readonly STORAGE_PREFIX = 'toggle-states-';

  /**
   * Loads toggle states from session storage
   * @param componentKey Unique key for the component (e.g., 'preview-tab')
   * @param defaultStates Default toggle states
   * @returns Loaded toggle states
   */
  loadStates(componentKey: string, defaultStates: ToggleStates): ToggleStates {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_PREFIX + componentKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultStates, ...parsed };
      }
    } catch (error) {
      console.warn(`Failed to load toggle states for '${componentKey}':`, error);
    }
    return defaultStates;
  }

  /**
   * Saves toggle states to session storage
   * @param componentKey Unique key for the component
   * @param states Current toggle states
   */
  saveStates(componentKey: string, states: ToggleStates): void {
    try {
      sessionStorage.setItem(this.STORAGE_PREFIX + componentKey, JSON.stringify(states));
    } catch (error) {
      console.warn(`Failed to save toggle states for '${componentKey}':`, error);
    }
  }

  /**
   * Generic toggle method that works with any boolean property
   * @param componentKey Unique key for the component
   * @param states Current toggle states object
   * @param toggleKey The key of the toggle to flip
   * @param callback Optional callback to execute after toggling
   */
  toggle<K extends keyof ToggleStates>(
    componentKey: string,
    states: ToggleStates,
    toggleKey: K,
    callback?: () => void
  ): void {
    states[toggleKey] = !states[toggleKey];
    this.saveStates(componentKey, states);
    if (callback) {
      callback();
    }
  }
}
